import { INVOKE_ID_HEADER } from '../../../constants/ApiConstants'
import { RESTMethods } from '../../../constants/REST'
import { IsStringEmpty } from '../../../utils/GlobalFunctions'
import { URLConstructor } from '../../../utils/URLBuilder'
import { RESTAPIError } from './RESTAPIError'
import { requestJSON } from './RESTClient'
import { StatusCodes } from '../../../constants/StatusCodes'
import { handleError } from './RESTErrorHandler'
import { JSONResponse, RequestGETOptions } from '../../Types/Types'
import { TimingOptions } from '../../TimingOptions'

export { RESTAPIError, StatusCodes }

const ALLOWED_NO_CONTENT_STATUS_CODES: number[] = [StatusCodes.CONTINUE, StatusCodes.OK_NO_CONTENT]
export const ALLOWED_OK_RESPONSES: number[] = [StatusCodes.OK, StatusCodes.OK_NO_CONTENT]
export const AUTHORISATION_ERROR: number[] = [StatusCodes.UNAUTHORISED, StatusCodes.FORBIDDEN]

/**
 * Processes HTTP response from API.
 *
 * Structure example of the Admin API.
 *  {
 *    "ErrorMessage": "Some error msg",
 *    "ErrorCode": "12345",
 *    "Data": {
 *      "Key": "Value",
 *      ...
 *    }
 *  }
 * @param {Response} response HTTP response.
 */
const processJSONResponse = async (response: Response, allowNoContent?: boolean): Promise<object | Response> => {
    try {
        if (~ALLOWED_NO_CONTENT_STATUS_CODES.indexOf(response.status)) {
            return response
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const JSONRes = await response.json()
        // eslint-disable-next-line
        const data = JSONRes.Data
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const errorMsg: string = (JSONRes.ErrorMessage || 'No data has been found in the response.') as string
        // eslint-disable-next-line
        const errorCode: string = JSONRes.ErrorCode as string

        if (!data && !allowNoContent) {
            throw new RESTAPIError(response.status, null, errorMsg, errorCode as OnlyNullable<string>)
        } else if (!data && allowNoContent) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return JSONRes
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return JSONRes
    } catch (error) {
        if ((error as Error).name === 'RESTAPIError') {
            throw error
        } else {
            // Wrap error so that we can handle that in application correctly.
            throw new RESTAPIError(response.status, null, response.statusText || 'Something went wrong!', null)
        }
    }
}

/**
 * Prepares HTTP options for the request.
 * @param requestOptions HTTP request options.
 */
// eslint-disable-next-line
const prepareHTTPOptions = (requestOptions: any): RequestInit => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!requestOptions.headers) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        requestOptions.headers = {}
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (requestOptions.json === true) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (typeof requestOptions.body === 'object') {
            // Stringify the object to send as request body.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            requestOptions.body = JSON.stringify(requestOptions.body)
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        requestOptions.headers['Content-Type'] = 'application/json'
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    requestOptions.credentials = 'include'

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return requestOptions
}

/**
 * The asynchronous HTTP request.
 * @param requestPath The request path.
 * @param HTTPMethod HTTP method.
 * @param body JSON object body request body.
 * @param headers HTTP headers object.
 * @param isJSON Whether the request has JSON body.
 *
 * @deprecated - Use requestJSON in the RESTClient.tsx file instead.
 */
export const Request = async (
    requestPath: string,
    HTTPMethod = 'GET',
    body: object | null = null,
    headers: object | null = null,
    isJSON: boolean | null = null,
    allowNoContent: boolean = false
    // eslint-disable-next-line
): Promise<any> => {
    const requestOptions = {
        method: HTTPMethod,
        body,
        headers,
        json: isJSON === false ? undefined : !!body
    }

    return fetch(`${requestPath}`, prepareHTTPOptions(requestOptions)).then((response: Response) =>
        processJSONResponse(response, allowNoContent)
    )
}

/**
 * Get JSON response from GET REST endpoint with caching mechanism.
 * <T> - The response type you are expecting from the server.
 *
 * @param requestPath - The path to request data from.
 * @param options - The REST GET options, this includes headers and cache ttl.
 *
 * @returns - Promise<JSONResponse<T>> - Promise of the data from the server in the generic type specified.
 */
export const requestGetJSON = <T extends unknown>(
    requestPath: URLConstructor,
    options?: RequestGETOptions
): Promise<JSONResponse<T>> =>
    requestJSON(requestPath, {
        method: RESTMethods.GET,
        headers: options?.headers,
        errorOnNoContent: options?.errorOnNoContent,
        cacheTime: options?.cacheTime,
        isStringResult: options?.isStringResult,
        timeout: options?.timeout
    })

/**
 * Perform asynchronous request with status checkups.
 *
 * @param requestPath - Initial request path.
 * @param statusURL - Status check request path.
 * @param body - Body to send.
 *
 * @return A promise that is fulfilled when the status either completes or times out.
 */
export const AsyncRequest = async (
    requestPath: string,
    statusURL: URLConstructor,
    body?: Nullable<object>,
    method?: string,
    timingOptions?: TimingOptions
): Promise<Response> =>
    new Promise(
        (
            resolve: (value: Response | PromiseLike<Response>) => void,
            reject: (value: Response | PromiseLike<Response> | RESTAPIError) => void
        ): void => {
            const { maxChecks, frequency, errorMessage }: TimingOptions = timingOptions ?? new TimingOptions()
            const initialResponsePromise: Promise<Response> = fetch(requestPath, {
                method: method ?? 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include'
            })

            initialResponsePromise.then((initialResponse: Response) => {
                // Sometimes the initial response will return the result.
                if (initialResponse.status === StatusCodes.OK) {
                    resolve(initialResponse)
                    return
                }

                if (!~ALLOWED_NO_CONTENT_STATUS_CODES.indexOf(initialResponse.status)) {
                    handleError(initialResponse).then((error: RESTAPIError) => reject(error))
                    return
                }

                const invokeID: Nullable<string> = initialResponse.headers.get(INVOKE_ID_HEADER)

                if (IsStringEmpty(invokeID)) {
                    reject(initialResponse)
                }

                let statusCheckCount: number = 0
                let isLooping: boolean = true

                const cancelLooping = (): void => {
                    isLooping = false
                }

                const statusCheck = async (): Promise<void> => {
                    statusCheckCount++

                    if (statusCheckCount <= maxChecks && isLooping) {
                        const checkResponse: Response = await fetch(
                            statusURL.appendPathParams(invokeID ?? '').constructURL()
                        )

                        switch (checkResponse.status) {
                            case StatusCodes.OK:
                            case StatusCodes.OK_NO_CONTENT:
                                cancelLooping()
                                resolve(checkResponse)
                                break
                            case StatusCodes.CONTINUE:
                                setTimeout((): void => {
                                    statusCheck()
                                }, frequency)
                                break
                            default:
                                cancelLooping()
                                reject(await handleError(checkResponse))
                                break
                        }
                    } else {
                        cancelLooping()
                        reject(new RESTAPIError(408, null, errorMessage, null))
                    }

                    return undefined
                }

                setTimeout((): void => {
                    statusCheck()
                }, frequency)
            }, reject)
        }
    )

export default Request
